import React, { useContext, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, Link } from "react-router-dom";
import { Col, Row } from "react-bootstrap";
import { Formik, Form as FormFormik } from "formik";
import { FiltersButton, ReactSelect, TableHeader } from "@services/ui-components";
import Spinner from "@services/ui-components/src/Spinner/Spinner";
import VioletBadge from "@services/ui-components/src/VioletBadge/VioletBadge";
import EmployeeServicesTable from "./EmployeeServicesTable/EmployeeServicesTable";
import CategoryInput from "./EmployeeServicesTable/CategoryInput";
import { useGetEmployeeByIdQuery } from "@me-team/host/main/addEmployee/graphql/addEmployee.hooks";
import { useReactiveVar } from "@apollo/client";
import { CompanyAccess, PermissionScope } from "@me-team/host/main/graphql/types";
import { currentBranchEmployeeIdVar, currentCompanyIdVar } from "@me-team/host/main/globalVar/state";
import { AccessContext } from "@me-team/host/src/components/App/App";
import { useRoleAccess } from "@me-team/host/src/utils/useRoleAccess";
import AccessService from "@me-team/host/services/AccessService";

const EmployeeServices: React.FC = () => {
    const { t } = useTranslation();
    const { id } = useParams();
    const { branchId } = useParams();
    const currentCompanyId = useReactiveVar(currentCompanyIdVar);
    const currentBranchEmployeeId = useReactiveVar(currentBranchEmployeeIdVar);
    const { isReady: isAccessContextReady } = useContext(AccessContext);
    const accessService = AccessService.getInstance();

    const [currentId, setCurrentId] = useState({
        categoryId: null,
    });

    const inputRef = useRef<HTMLInputElement>(null);
    const isOwnEmployeeServicesPage = Boolean(currentBranchEmployeeId && id && currentBranchEmployeeId === Number(id));
    const employeeServicesScope = isOwnEmployeeServicesPage ? PermissionScope.Self : PermissionScope.All;
    const isEmployeeServicesAccessPending =
        !isAccessContextReady || !accessService.isRoleAccessReady() || accessService.isRoleAccessLoading();
    const canViewEmployeeServices = useRoleAccess({
        permission: CompanyAccess.EmployeeServicesView,
        scope: employeeServicesScope,
    });
    const canViewEmployeePage = useRoleAccess({
        permission: CompanyAccess.EmployeeView,
        scope: employeeServicesScope,
    });
    const canEditEmployeePage = useRoleAccess({
        permission: CompanyAccess.EmployeeEdit,
        scope: employeeServicesScope,
    });
    const canEditEmployeeServices = useRoleAccess({
        permission: CompanyAccess.EmployeeServicesEdit,
        scope: employeeServicesScope,
    });
    const canViewSalaryAll = useRoleAccess(CompanyAccess.SalarySettingsView, { scope: PermissionScope.All });
    const canViewSalarySelf = useRoleAccess(CompanyAccess.SalarySettingsView, { scope: PermissionScope.Self });
    const canEditSalaryAll = useRoleAccess(CompanyAccess.SalarySettingsEdit, { scope: PermissionScope.All });
    const canEditSalarySelf = useRoleAccess(CompanyAccess.SalarySettingsEdit, { scope: PermissionScope.Self });
    const canOpenServiceLink = useRoleAccess({
        permission: CompanyAccess.ServiceListView,
    });
    const canEditInlineServiceFields = canEditEmployeeServices;
    const canViewSalarySettings = isOwnEmployeeServicesPage
        ? canViewSalaryAll || canViewSalarySelf || canEditSalaryAll || canEditSalarySelf
        : canViewSalaryAll || canEditSalaryAll;
    const canEditSalarySettings = isOwnEmployeeServicesPage ? canEditSalaryAll || canEditSalarySelf : canEditSalaryAll;
    const canAccessSalarySettings = canViewSalarySettings || canEditSalarySettings;
    const employeeServicesPageAccessMode = canViewEmployeeServices
        ? canEditEmployeeServices
            ? "edit"
            : "view"
        : "forbidden";
    const employeeServicesAccessContract = {
        isOwnEmployeeServicesPage,
        employeeServicesScope,
        isEmployeeServicesAccessPending,
        canViewEmployeeServices,
        canEditEmployeeServices,
        canOpenServiceLink,
        canEditInlineServiceFields,
        canAccessSalarySettings,
        canEditSalarySettings,
        employeeServicesPageAccessMode,
    };

    const [showFilters, setShowFilters] = useState(false);
    const [filter, setFilter] = useState({
        serviceName: null,
        categoryId: currentId.categoryId,
        accessibility: null,
        onlineBooking: null,
    });

    const [currentFilter, setCurrentFilter] = useState({
        categoryId: currentId.categoryId,
        accessibility: null,
        onlineBooking: null,
    });
    const [currentName, setCurrentName] = useState({
        categoryName: "",
        subCategoryName: "",
    });
    useEffect(() => {
        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    }, []);
    const { data } = useGetEmployeeByIdQuery({
        variables: {
            employeeId: +id,
            companyId: +currentCompanyId,
        },
        skip:
            !currentCompanyId ||
            employeeServicesAccessContract.isEmployeeServicesAccessPending ||
            employeeServicesAccessContract.employeeServicesPageAccessMode === "forbidden",
    });
    const employee = data?.user.company.employees.employees[0];

    const toggleFilters = () => setShowFilters(!showFilters);

    const handleAvailabilityChange = (selectedOption: { value: boolean; label: string } | null) => {
        setCurrentFilter((prevFilter) => ({
            ...prevFilter,
            accessibility: selectedOption ? selectedOption.value : null,
        }));
    };

    const handleOnlineBookingChange = (selectedOption: { value: boolean; label: string } | null) => {
        setCurrentFilter((prevFilter) => ({
            ...prevFilter,
            onlineBooking: selectedOption ? selectedOption.value : null,
        }));
    };

    const normalizeQuery = (s?: string) => (s ?? "").replace(/\s+/g, " ").trim();

    const applyFilters = () => {
        const raw = inputRef.current?.value ?? "";
        const serviceName = normalizeQuery(raw);
        if (inputRef.current) inputRef.current.value = serviceName;

        setFilter({
            serviceName: inputRef.current.value || null,
            categoryId: currentId.categoryId,
            accessibility: currentFilter.accessibility,
            onlineBooking: currentFilter.onlineBooking,
        });
    };

    const clearFilters = () => {
        setCurrentName({
            categoryName: "",
            subCategoryName: "",
        });
        setCurrentFilter({
            serviceName: null,
            categoryId: null,
            accessibility: null,
            onlineBooking: null,
        });
        setFilter({
            serviceName: null,
            categoryId: null,
            accessibility: null,
            onlineBooking: null,
        });
        if (inputRef.current) {
            inputRef.current.value = "";
        }
    };

    const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            e.preventDefault();
            applyFilters();
        }
    };

    if (!currentCompanyId || employeeServicesAccessContract.isEmployeeServicesAccessPending) {
        return <Spinner />;
    }

    return (
        <>
            <VioletBadge
                text={t(
                    "On this page, you can select the services provided by this employee. You can specify different durations and prices for the services for this employee."
                )}
            />

            <TableHeader
                title={`${t("Employee's services")} «${employee?.name || ""}  ${employee?.surname || ""}»`}
                paths={[
                    { path: `/employees-list/employee-services/${id}/${branchId}`, label: t("Personal") },
                    { path: `/employees-list/${branchId}`, label: t("Employee List") },
                    {
                        path: `/employees-list/employee-services/${id}/${branchId}`,
                        label: `${t("Employee's services")} «${employee?.name || ""}  ${employee?.surname || ""}»`,
                    },
                ]}
                buttons={
                    <Row className="justify-content-end gx-3 mt-3 mt-lg-0">
                        <Col xs={6} sm={4} lg={6} xxl className="mb-3 mb-xxl-0">
                            {canViewEmployeePage || canEditEmployeePage ? (
                                <Link
                                    to={`/employees-list/edit-employee/${id}/${branchId}`}
                                    className="btn btn-orange px-1 w-100 text-capitalize fw-bold"
                                >
                                    {t("To employee")}
                                </Link>
                            ) : null}
                        </Col>
                        <Col xs={6} sm={4} lg={6} xxl>
                            <Link
                                to={`/employees-list/${branchId}`}
                                className="btn btn-outline-orange px-1 w-100 text-capitalize"
                            >
                                {t("All employees")}
                            </Link>
                        </Col>
                        <Col sm={4} lg={6} xxl={3}>
                            <FiltersButton filter={filter} showFilters={showFilters} onClick={toggleFilters} />
                        </Col>
                    </Row>
                }
                showFilters={showFilters}
                colSize={7}
                btnColSize={5}
            >
                <Row className="mb-3 gx-3">
                    <Col xs={12} sm={6} xxl className="mb-3">
                        <input
                            type="text"
                            name="name"
                            className="form-control py-6"
                            placeholder={t("Search by service name")}
                            ref={inputRef}
                            id="employeeServices-search-field"
                            onKeyDown={handleSearchKeyDown}
                        />
                    </Col>
                    <Col xs={12} sm={6} xxl className="mb-3">
                        <div className="position-relative">
                            <Formik initialValues={{ Name: "" }}>
                                {(formik) => (
                                    <FormFormik onSubmit={formik.handleSubmit}>
                                        <CategoryInput
                                            setCurrentId={setCurrentId}
                                            currentId={currentFilter}
                                            setCurrentName={setCurrentName}
                                            currentName={currentName}
                                        />
                                    </FormFormik>
                                )}
                            </Formik>
                        </div>
                    </Col>
                    <Col xs={12} sm={6} xxl className="mb-3">
                        <ReactSelect
                            name="employeeServices-availability-field"
                            id="employeeServices-availability-field"
                            options={[
                                { value: true, label: t("Yes") },
                                { value: false, label: t("No") },
                            ]}
                            placeholder={t("Availability")}
                            onChange={handleAvailabilityChange}
                            isSearchable={false}
                            value={
                                currentFilter.accessibility !== null
                                    ? {
                                          value: currentFilter.accessibility,
                                          label: currentFilter.accessibility ? t("True") : t("False"),
                                      }
                                    : null
                            }
                        />
                    </Col>
                    <Col xs={12} sm={6} xxl className="mb-3">
                        <ReactSelect
                            name="employeeServices-onlineBooking-field"
                            id="employeeServices-onlineBooking-field"
                            options={[
                                { value: true, label: t("True") },
                                { value: false, label: t("False") },
                            ]}
                            placeholder={t("Online Booking")}
                            onChange={handleOnlineBookingChange}
                            isSearchable={false}
                            value={
                                currentFilter.onlineBooking !== null
                                    ? {
                                          value: currentFilter.onlineBooking,
                                          label: currentFilter.onlineBooking ? t("True") : t("False"),
                                      }
                                    : null
                            }
                        />
                    </Col>
                    <Col xs={12} xxl className="d-flex gap-3 mb-3">
                        <button className="btn btn-outline-dark border-grey fw-normal w-100" onClick={clearFilters}>
                            {t("clear")}
                        </button>
                        <button className="btn btn-primary w-100" onClick={applyFilters}>
                            {t("Apply")}
                        </button>
                    </Col>
                </Row>
            </TableHeader>
            <EmployeeServicesTable
                filter={filter}
                canEditEmployeeServices={employeeServicesAccessContract.canEditEmployeeServices}
                canOpenServiceLink={employeeServicesAccessContract.canOpenServiceLink}
                canEditInlineServiceFields={employeeServicesAccessContract.canEditInlineServiceFields}
                canAccessSalarySettings={employeeServicesAccessContract.canAccessSalarySettings}
                canEditSalarySettings={employeeServicesAccessContract.canEditSalarySettings}
            />
        </>
    );
};

export default EmployeeServices;
